import { IStructureGraph, IStructureLane, IStructureNode } from './types';

/**
 * Company lanes, derived from graph reachability (FD-4465, spec §4.1).
 *
 * BE confirmed (2026-08-17) that lane membership is not coming from the payload and
 * the client should infer it, pending Chandra on whether anything else lands.
 *
 * A vehicle belongs to a company's lane when **that company is the only portfolio
 * company it reaches**. A vehicle feeding two companies is firm-level: putting it in
 * one lane would misrepresent it as dedicated to that deal, which is exactly the
 * thing the lanes are meant to communicate.
 */

/** Portfolio companies reachable downward from each node. */
function reachableCompanies(graph: IStructureGraph): Map<string, Set<string>> {
  const childrenByParent = new Map<string, string[]>();
  graph.edges.forEach((edge) => {
    childrenByParent.set(edge.source, [...(childrenByParent.get(edge.source) ?? []), edge.target]);
  });

  const isCompany = new Set(
    graph.nodes.filter((node) => node.role === 'PORTFOLIO_COMPANY').map((node) => node.id)
  );

  const memo = new Map<string, Set<string>>();

  // Iterative post-order with an explicit stack: a deep chain would blow the call
  // stack on a recursive walk, and the graph is a DAG so nodes are revisited often.
  const visit = (start: string): Set<string> => {
    const stack: { id: string; expanded: boolean }[] = [{ id: start, expanded: false }];
    const inProgress = new Set<string>();

    while (stack.length > 0) {
      const frame = stack[stack.length - 1];
      if (memo.has(frame.id)) {
        stack.pop();
        continue;
      }

      if (!frame.expanded) {
        frame.expanded = true;
        inProgress.add(frame.id);
        (childrenByParent.get(frame.id) ?? []).forEach((child) => {
          // Cycles should not occur in a DAG, but a bad edge must not hang the UI.
          if (!memo.has(child) && !inProgress.has(child)) {
            stack.push({ id: child, expanded: false });
          }
        });
        continue;
      }

      const companies = new Set<string>();
      if (isCompany.has(frame.id)) {
        companies.add(frame.id);
      }
      (childrenByParent.get(frame.id) ?? []).forEach((child) => {
        memo.get(child)?.forEach((company) => companies.add(company));
      });

      memo.set(frame.id, companies);
      inProgress.delete(frame.id);
      stack.pop();
    }

    return memo.get(start) ?? new Set();
  };

  graph.nodes.forEach((node) => visit(node.id));

  return memo;
}

export interface IDerivedLanes {
  lanes: IStructureLane[];
  /** Node id to lane id, for nodes that sit in exactly one company's lane. */
  laneByNodeId: Map<string, string>;
}

export function deriveLanes(graph: IStructureGraph): IDerivedLanes {
  const companiesByNode = reachableCompanies(graph);
  const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));
  const laneByNodeId = new Map<string, string>();
  const usedCompanyIds = new Set<string>();

  graph.nodes.forEach((node) => {
    const companies = companiesByNode.get(node.id);
    if (!companies || companies.size !== 1) {
      return;
    }
    const [companyId] = [...companies];
    laneByNodeId.set(node.id, `lane-${companyId}`);
    usedCompanyIds.add(companyId);
  });

  const lanes: IStructureLane[] = [...usedCompanyIds].map((companyId) => ({
    id: `lane-${companyId}`,
    label: nodesById.get(companyId)?.name ?? companyId,
  }));

  return { lanes, laneByNodeId };
}

/** Applies derived lanes to the graph's nodes. */
export function withDerivedLanes(graph: IStructureGraph): IStructureGraph {
  const { lanes, laneByNodeId } = deriveLanes(graph);

  const nodes: IStructureNode[] = graph.nodes.map((node) => ({
    ...node,
    laneId: laneByNodeId.get(node.id),
  }));

  return { ...graph, nodes, lanes };
}
